import { CONCEPTS } from '@/constants/scenarios';
import { Colors } from '@/constants/theme';
import { BenchmarkingItem, useBenchmarkingStore } from '@/stores/benchmarking-store';
import { BenchmarkingFormValues, getBenchmarkingItemSchema } from '@/types/benchmarking.schema';
import { Country } from '@/types/country';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text } from 'react-native';
import { BenchmarkingItemResult } from './benchmarking-item-result';
import { CountrySelect } from './country-select';
import { Card } from './ui/card';
import { IconSymbol } from './ui/icon-symbol';
import { Select } from './ui/select';
import { Pressable, View } from './ui/tw-wrappers';

interface BenchmarkingItemFormProps {
  item: BenchmarkingItem;
  idx: number;
  itemsLength: number;
  countries: Country[];
  commonProps: { vendorIdList: string[] };
}

export function BenchmarkingItemForm({ item, idx, itemsLength, commonProps, countries }: BenchmarkingItemFormProps) {
  const { t } = useTranslation();
  const updateBenchmarking = useBenchmarkingStore(s => s.updateBenchmarking);
  const removeBenchmarking = useBenchmarkingStore(s => s.removeBenchmarking);

  const schema = useMemo(() => getBenchmarkingItemSchema(t), [t]);

  const {
    control,
    watch,
    formState: { errors },
    clearErrors,
    setValue,
    reset,
  } = useForm<BenchmarkingFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerId: item.customerId ?? '',
      vendorId: item.vendorId ?? '',
      localCurrency: item.localCurrency ?? 'USD',
      invoicingConcept: item.invoicingConcept ?? undefined,
    },
    mode: 'onTouched',
  });

  // Sync prop (store) -> form (needed since form state is internal)
  useEffect(() => {
    reset({
      customerId: item.customerId ?? '',
      vendorId: item.vendorId ?? '',
      localCurrency: item.localCurrency ?? 'USD',
      invoicingConcept: item.invoicingConcept ?? undefined,
    });
  }, [item.customerId, item.vendorId, item.localCurrency, item.invoicingConcept, reset]);

  // Sync form changes → store
  useEffect(() => {
    const sub = watch(values => {
      updateBenchmarking(item.id, {
        customerId: values.customerId || undefined,
        vendorId: values.vendorId || undefined,
        localCurrency: values.localCurrency ?? 'USD',
        invoicingConcept: values.invoicingConcept as any,
      });
    });
    return () => sub.unsubscribe();
  }, [watch, item.id, updateBenchmarking]);

  const watchedVendorId = watch('vendorId');

  return (
    <View>
      <Card className='w-93' backgroundColor={'white'}>
        <View style={styles.colCard}>
          <View style={styles.colTop}>
            <Text style={styles.colTitle}>{`${t('screens.benchmarking.scenario')} ${idx + 1}`}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {itemsLength > 1 && (
                <Pressable onPress={() => removeBenchmarking(item.id)} style={styles.smallBtn}>
                  <IconSymbol name='trash' color={Colors.light.error} />
                </Pressable>
              )}
            </View>
          </View>

          <View className='flex-col gap-4 mb-2'>
            {/* vendorId - Revenue Recognition Country */}
            <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='vendorId'
                render={({ field: { value, onChange } }) => (
                  <CountrySelect
                    id={`vendorId-${item.id}`}
                    name='vendorId'
                    label={t('onboarding.fields.vendorCountry')}
                    tooltip={t('onboarding.fields.vendorCountryTooltip')}
                    valueId={value || null}
                    // excludeIds={commonProps.vendorIdList}
                    hideClearButton
                    placeholder={t('onboarding.fields.searchPlaceholder', 'Search destination…')}
                    onChange={c => {
                      const id = c?.id ?? '';
                      onChange(id);
                      if (id) clearErrors('vendorId');
                      // reset customerId if same country is selected
                      if (watchedVendorId && id === watch('customerId')) {
                        setValue('vendorId', '', { shouldValidate: true });
                      }
                    }}
                    countries={countries.filter(c => commonProps.vendorIdList?.includes(c.id))}
                  />
                )}
              />
              {errors.vendorId ? <Text style={styles.errorText}>{errors.vendorId.message}</Text> : null}
            </View>
            {/* customerId - Expansion country */}
            <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='customerId'
                render={({ field: { value, onChange } }) => (
                  <CountrySelect
                    id={`customerId-${item.id}`}
                    name='customerId'
                    label={t('onboarding.fields.customerCountry')}
                    tooltip={t('onboarding.fields.customerCountryTooltip')}
                    hideClearButton
                    excludeIds={commonProps.vendorIdList}
                    valueId={value || null}
                    countries={countries}
                    onChange={c => {
                      const id = c?.id ?? '';
                      onChange(id);
                      if (id) clearErrors('customerId');
                      // // reset vendorId if same country is selected
                      // if (watchedCustomerId && id === watch('vendorId')) {
                      //   setValue('vendorId', '', { shouldValidate: true });
                      // }
                    }}
                  />
                )}
              />
              {errors.customerId ? <Text style={styles.errorText}>{errors.customerId.message}</Text> : null}
            </View>

            {/* invoicingConcept */}
            <View style={styles.fieldGroup}>
              <Controller
                control={control}
                name='invoicingConcept'
                render={({ field: { value, onChange } }) => (
                  <Select
                    id={`invoicingConcept-${item.id}`}
                    name='invoicingConcept'
                    label={t('onboarding.fields.invoicingConcept')}
                    tooltip={t('onboarding.fields.invoicingConceptTooltip')}
                    value={value}
                    onValueChange={onChange}
                    options={CONCEPTS.map(c => ({
                      value: c.key,
                      label: t(`${c.i18nKey}.label`),
                      tooltip: t(`${c.i18nKey}.description`),
                    }))}
                    placeholder={t('common.select', 'Select...')}
                  />
                )}
              />
              {errors.invoicingConcept ? <Text style={styles.errorText}>{errors.invoicingConcept.message}</Text> : null}
            </View>
          </View>
        </View>
      </Card>
      <BenchmarkingItemResult values={watch()} index={idx} />
    </View>
  );
}

const styles = StyleSheet.create({
  colCard: {
    height: '100%',
    borderRadius: 18,
    padding: 14,
    gap: 4,
  },
  colTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    height: 40,
  },
  colTitle: {
    fontSize: 16,
    fontWeight: '800',
  },
  smallBtn: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  fieldGroup: {
    gap: 10,
  },
  errorText: {
    color: Colors.light.error,
    fontSize: 14,
    textAlign: 'center',
  },
});
